import React, { createContext, useContext, useState, ReactNode } from 'react';

const THEME_STORAGE_KEY = 'gym_notebook_theme';

// Theme interface
export interface Theme {
  id: string;
  name: string;
  description: string;
  gradient: string;
  headerBg: string;
  cardBg: string;
  accent: string;
  accentBg: string;
  border: string;
}

// Preset themes with gradient backgrounds
export const THEMES: Record<string, Theme> = {
  default: {
    id: 'default',
    name: 'Midnight',
    description: 'Classic dark theme',
    gradient: 'from-slate-900 via-slate-800 to-slate-900',
    headerBg: 'bg-[#0a0e14]/80',
    cardBg: 'bg-[#1b2028]',
    accent: 'text-yellow-400',
    accentBg: 'bg-yellow-500',
    border: 'border-[#20262f]',
  },
  darkBlue: {
    id: 'darkBlue',
    name: 'Deep Ocean',
    description: 'Calm blue depths',
    gradient: 'from-slate-900 via-blue-900 to-slate-900',
    headerBg: 'bg-blue-900/80',
    cardBg: 'bg-blue-800/30',
    accent: 'text-cyan-400',
    accentBg: 'bg-cyan-500',
    border: 'border-blue-700',
  },
  forestGreen: {
    id: 'forestGreen',
    name: 'Forest',
    description: 'Natural green vibes',
    gradient: 'from-slate-900 via-emerald-900 to-slate-900',
    headerBg: 'bg-emerald-900/80',
    cardBg: 'bg-emerald-800/30',
    accent: 'text-lime-400',
    accentBg: 'bg-lime-500',
    border: 'border-emerald-700',
  },
  sunsetOrange: {
    id: 'sunsetOrange',
    name: 'Sunset',
    description: 'Warm orange glow',
    gradient: 'from-slate-900 via-orange-900 to-slate-900',
    headerBg: 'bg-orange-900/80',
    cardBg: 'bg-orange-800/30',
    accent: 'text-amber-400',
    accentBg: 'bg-amber-500',
    border: 'border-orange-700',
  },
  purpleNight: {
    id: 'purpleNight',
    name: 'Purple Night',
    description: 'Mystical purple',
    gradient: 'from-slate-900 via-purple-900 to-slate-900',
    headerBg: 'bg-purple-900/80',
    cardBg: 'bg-purple-800/30',
    accent: 'text-pink-400',
    accentBg: 'bg-pink-500',
    border: 'border-purple-700',
  },
};

interface ThemeContextValue {
  theme: Theme;
  setTheme: (id: string) => void;
  themeId: string;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: THEMES.default,
  setTheme: () => {},
  themeId: 'default',
});

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  // Use lazy initialization to read from localStorage
  const [themeId, setThemeId] = useState<string>(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return (saved && THEMES[saved]) ? saved : 'default';
  });

  // Save theme to localStorage when changed
  const setTheme = (id: string) => {
    if (THEMES[id]) {
      setThemeId(id);
      localStorage.setItem(THEME_STORAGE_KEY, id);
    }
  };

  const theme = THEMES[themeId] || THEMES.default;

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themeId }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}

export default ThemeProvider;
